import React, { useEffect, useState } from 'react';
import Loader from "../../components/Loader"; 
import Paginator from "../../components/Paginator"; 
import ApiService from "../../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {formatDate} from "../../utils/helper";


const List = () => {
    
    const [isLoader, setIsLoader] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [pagination, setPagination] = useState({});
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    
    // Initialize state from URL params
    const [apiData, setApiData] = useState({
        search: searchParams.get('search') || "",
        daterange: searchParams.get('daterange') ? 
            JSON.parse(searchParams.get('daterange')) : [],
        page: parseInt(searchParams.get('page')) || 1,
    });
    
    // Update URL when filters change
    useEffect(() => {
        const params = new URLSearchParams();
        if (apiData.search) params.set('search', apiData.search);
        if (apiData.daterange && apiData.daterange.length > 0) params.set('daterange', JSON.stringify(apiData.daterange));
        if (apiData.page > 1) params.set('page', apiData.page.toString());
        
        navigate(`?${params.toString()}`, { replace: true });
    }, [apiData.search, apiData.daterange, apiData.page]);

    
    useEffect(() => {
        getNotifications();
    }, [apiData.page, apiData.search, apiData.daterange]);

    const getNotifications = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "custom-notifications",
                data: apiData
            });
            const data = response.data;
            if (data.status) {
                setNotifications(data.data);
                setPagination(data.pagination);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            console.log(error);
            setIsLoader(false);
        }
    };

    const handlePageChange = (event) => {
        setApiData(prev => ({ ...prev, page: event }));
    };

    const clearFilter = () => {
        setApiData({
            search: '',
            daterange: [],
            page: 1
        });
    };

    const delData = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'you will not be able to revert it!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            const response = await ApiService.request({
                method: "DELETE",
                url: `custom-notifications/${id}`,
            });
            const data = response.data;
            if (data.status) {
                getNotifications();
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0 text-capitalize">
                           Push Notifications</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Push Notifications</li>
                        </ul>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/custom-notifications/send" className='btn btn-base btn-sm'>
                            <i className="bi bi-send-fill me-2"></i>Send New Notification
                        </Link>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <form method="get">
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="d-flex align-items-center position-relative my-1">
                                                <input 
                                                    type="text" 
                                                    value={apiData.search} 
                                                    onChange={e => setApiData({ ...apiData, search: e.target.value })} 
                                                    className="form-control form-control-solid me-5"
                                                    placeholder="Search title or message.." 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <DatePicker
                                                selectsRange={true}
                                                startDate={apiData.daterange[0]}
                                                endDate={apiData.daterange[1]}
                                                onChange={e => setApiData({ ...apiData, daterange: e })} 
                                                isClearable={true}
                                                maxDate={new Date()}
                                                className='form-control form-control-solid'
                                                placeholderText="Select Date Range"
                                            />
                                        </div>
                                        <div className="col-md-3 mt-2">
                                            <button 
                                                type="button" 
                                                onClick={clearFilter} 
                                                className={`btn btn-sm btn-secondary mx-1 ${!apiData.search && !apiData.daterange.length ? 'd-none' : ''}`}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <div id="kt_notifications_table_wrapper" className="dataTables_wrapper dt-bootstrap4 no-footer">
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                        <thead>
                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                <th>Title</th>
                                                <th>Message</th>
                                                <th>Recipient Type</th>
                                                <th>Total Sent</th>
                                                <th>Total Failed</th>
                                                <th>Sent By</th>
                                                <th>Created Date</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="fw-semibold text-gray-600 fs-6">
                                            {notifications.length > 0 ? notifications.map((notification, index) => (
                                                <tr key={index}>
                                                    <td>{notification.title}</td>
                                                    <td>
                                                        <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {notification.message}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${notification.recipient_type === 'all' ? 'badge-light-primary' : 'badge-light-info'}`}>
                                                            {notification.recipient_type === 'all' ? 'All Users' : 'Specific Users'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="badge badge-light-success">
                                                            {notification.total_sent || 0}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="badge badge-light-danger">
                                                            {notification.total_failed || 0}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {notification.sent_by?.name || 'N/A'}
                                                    </td>
                                                    <td>{formatDate(notification.createdAt, 'DD MMM YYYY, hh:mm A')}</td>
                                                    <td className="mt-2">
                                                        <Link 
                                                            to={`/custom-notifications/${notification._id}`} 
                                                            className="btn btn-sm h-30px btn-light-primary me-2"
                                                        >
                                                            <i className="bi bi-eye-fill fs-6"></i>
                                                        </Link>
                                                        <button  
                                                            onClick={() => delData(notification._id)} 
                                                            className="btn btn-sm h-30px btn-light-danger"
                                                        >
                                                            <i className="bi bi-trash-fill fs-6"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="8" className="text-dark text-center mt-2">No data found!!</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table> 
                                    {isLoader && (
                                        <Loader text="Loading" size="30" className="text-center"  />
                                    )}

                                    {pagination.total_pages > 1 && (
                                        <Paginator
                                            page={pagination.current_page}
                                            rows={pagination.per_page}
                                            totalRecords={pagination.total_records}
                                            onPageChange={handlePageChange}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;

